import React, { useContext, useEffect, useState } from "react";
import { DataGrid, Column, Scrolling } from "devextreme-react/data-grid";
import { IconButton, Link } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { createSitesClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { AppName, dataGridConfig } from "../../../constants";
import { convertUTCTimeToLocal } from "../../../util/Utils";
import { isEmpty, isNull } from "lodash";
import { ModalProcessDetail } from "./ModalProcessDetails";
import { useDataGridColumnWidths } from "../../../hooks/useDataGridColumnWidths";

interface ProcessListTabProps {
  siteId: number | string | null;
  appName: string;
}

export const ProcessListTab = ({ siteId, appName }: ProcessListTabProps) => {
  const { user } = useContext(OidcIdentityContext);
  const userEmail = user?.profile.preferred_username!;

  const [processExecData, setProcessExecData] = useState<any>({
    pending: false,
    data: [],
  });
  const [isDisabled, setDisabled] = useState<boolean>(false);
  const [isDisplayDetailsModal, setIsDisplayDetailsModal] = useState(false);
  const [processId, setProcessId] = useState<string>();

  const { gridRef, onColumnResized, onInitialized } = useDataGridColumnWidths({
    gridId: `processListTab-${siteId}`,
    defaultColumnWidths: {
      0: 400, // process column
      1: 100, // Run Process column
      2: 80,  // Status column
      3: 400, // Message column
      4: 200, // Initiated date and time column
      5: 200, // Completed date and time column
      6: 150, // Initiated by column
    },
  });

  const fetchProcessExecutionData = async () => {
    try {
      if (!siteId) {
        setProcessExecData({ pending: false, data: [] });
        return;
      }
      setProcessExecData((prev: any) => ({ ...prev, pending: true }));
      const result = await createSitesClientApi.taxportal_GetSiteProcessExecutionDetails(
        String(siteId),
        appName
      );
      const processData = result?.data?.list ?? [];
      const visibleProcessData = appName === AppName.IS
        ? processData.filter(
            (process: any) =>
              process?.processDescription?.trim().toLowerCase() === "sync isheet"
          )
        : processData;
      setProcessExecData({ pending: false, data: visibleProcessData });
    } catch (error) {
      setProcessExecData({ pending: false, data: [] });
    }
  };

  useEffect(() => {
    fetchProcessExecutionData();
  }, [siteId, appName]);

  const handleRunProcess = async (celldata: any) => {
    setDisabled(true);
    try {
      const payload: any = { processExecutionId: celldata.id };
      const response = await createSitesClientApi.taxportal_retryStep(
        celldata.id,
        payload,
        userEmail
      );
      if (response) {
        fetchProcessExecutionData();
      }
    } finally {
      setDisabled(false);
    }
  };

  const handleSeeDetailsButton = (id: string) => {
    setProcessId(id);
    setIsDisplayDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setProcessId("");
    setIsDisplayDetailsModal(false);
  };

  const renderRun = ({ row: { data } }: any) => {
    return (
      <Link
        component="button"
        variant="body2"
        disabled={isDisabled}
        onClick={() => handleRunProcess(data)}
      >
        Run
      </Link>
    );
  };

  const renderDetails = ({ row: { data } }: any) => {
    return data.status ? (
      <IconButton
        className="dx-datagrid dx-link"
        aria-label="See Details"
        title="See Details"
        onClick={() => handleSeeDetailsButton(data.id)}
        sx={{ padding: "2px", marginTop: "-5px", marginBottom: "-5px", fontSize: "16px" }}
      >
        <SearchIcon />
      </IconButton>
    ) : null;
  };

  const createdDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.processStarted === null) return null;
      return (
        <div style={{ display: "flex" }}>
          <div>{convertUTCTimeToLocal(e.data.processStarted)}</div>
        </div>
      );
    }
    return null;
  };

  const modifiedDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.processEnded === null) return null;
      return (
        <div style={{ display: "flex" }}>
          <div>{convertUTCTimeToLocal(e.data.processEnded)}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <DataGrid
        ref={gridRef}
        {...dataGridConfig}
        dataSource={processExecData?.data}
        showBorders={true}
        allowColumnResizing={true}
        showRowLines={true}
        columnAutoWidth={true}
        height={"100%"}
        onContentReady={onInitialized}
        onOptionChanged={(e) => {
          if (e.fullName?.includes(".width") && e.value !== e.previousValue) {
            onColumnResized(e);
          }
        }}
        noDataText={processExecData.pending ? "Loading..." : "No data"}
      >
        <Scrolling
          mode="virtual"
          scrollByContent={true}
          rowRenderingMode="virtual"
          showScrollbar={true}
        />
        <Column dataField="processDescription" width={400} caption="Process Description" allowSorting={true} />
        <Column width={100} dataField="Run Process" cellRender={renderRun} />
        <Column width={80} dataField="status" caption="Status" />
        <Column width={400} dataField="statusMessage" caption="Message" />
        <Column width={200} cellRender={createdDateTime} dataField="processStarted" caption="Initiated date and time" />
        <Column width={200} cellRender={modifiedDateTime} dataField="processEnded" caption="Completed date and time" />
        <Column dataField="createdBy" caption="Initiated by"
        calculateCellValue={(rowData: any) => rowData?.lastModifiedBy || rowData?.createdBy || ""} />
        <Column cellRender={renderDetails} type="buttons" caption="Execution detail" />
      </DataGrid>

      <ModalProcessDetail
        siteId={processId}
        isDisplayModal={isDisplayDetailsModal}
        handleClose={handleCloseDetailsModal}
      />
    </>
  );
};